import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ArrowLeft, ArrowRight, XCircle, Save, RefreshCw, CheckCircle2, Circle } from 'lucide-react';
import { ETAPE, statusLabel, STATUS_BY_STAGE, STATUS_LABELS, ACTE_LABELS } from './crmLabels';
import RenuntaDialog from './RenuntaDialog';
import DosarePanel from './DosarePanel';
import CoDebitoriPanel from './CoDebitoriPanel';
import CreditCard from './CreditCard';
import { toast } from 'sonner';

export default function ClientFisa({ api, settings, clientId, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [sol, setSol] = useState(null);
  const [renunta, setRenunta] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const d = await api.get(`/clients/${clientId}`);
    setData(d);
    const active = d.solicitari.find((s) => s.id === d.client.activeSolicitareId) || d.solicitari[0];
    setSol(active ? { ...active } : null);
  }, [api, clientId]);

  useEffect(() => { load(); }, [load]);

  if (!data || !sol) return <p className="text-sm text-gray-500">Se încarcă…</p>;

  const etapa = sol.etapa;
  const setField = (k, v) => setSol((p) => ({ ...p, [k]: v }));
  const setPas = (k, v) => setSol((p) => ({ ...p, urmatorulPas: { ...(p.urmatorulPas || {}), [k]: v } }));
  const setActa = (k, v) => setSol((p) => ({
    ...p, acte: { ...p.acte, [k]: { ...(p.acte?.[k] || {}), semnat: v, data: v ? new Date().toISOString().slice(0, 10) : null } },
  }));
  const toggleBanca = (b) => setSol((p) => {
    const arr = p.banciAlese || [];
    return { ...p, banciAlese: arr.includes(b) ? arr.filter((x) => x !== b) : [...arr, b] };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/solicitari/${sol.id}`, {
        status: sol.status, urmatorulPas: sol.urmatorulPas, tipCredit: sol.tipCredit,
        sumaSolicitata: sol.sumaSolicitata ? Number(sol.sumaSolicitata) : null,
        pretImobil: sol.pretImobil ? Number(sol.pretImobil) : null,
        avans: sol.avans ? Number(sol.avans) : null,
        analizaIntermediar: sol.analizaIntermediar, banciAlese: sol.banciAlese, acte: sol.acte,
      });
      toast.success('Salvat');
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const convert = async () => {
    try {
      await api.post(`/solicitari/${sol.id}/convert`);
      toast.success('Avansat în etapa următoare');
      await load(); onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Conversia nu este permisă');
    }
  };

  const solicitareNoua = async () => {
    try {
      await api.post(`/clients/${clientId}/solicitare-noua`);
      toast.success('Solicitare nouă creată (lead portofoliu)');
      await load(); onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare');
    }
  };

  const doRenunta = async (motiv) => {
    await api.post(`/solicitari/${sol.id}/renunta`, { motiv });
    toast.success('Client arhivat (renunțare)');
    await load(); onChanged && onChanged();
  };

  const c = data.client;
  const banci = settings?.banci || [];

  return (
    <div data-testid="crm-fisa" className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="crm-fisa-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Înapoi
        </Button>
        <h2 className="text-xl font-bold">{c.nume} {c.prenume}</h2>
        <Badge className="bg-blue-100 text-blue-700">{ETAPE[etapa]}</Badge>
        <Badge variant="outline">{statusLabel(sol.status)}</Badge>
        {sol.tipIntrare === 'portofoliu' && <Badge className="bg-purple-100 text-purple-700">portofoliu</Badge>}
        <div className="ml-auto flex gap-2">
          {(etapa === 1 || etapa === 2) && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={convert} data-testid="crm-fisa-convert">
              <ArrowRight className="h-4 w-4 mr-1" /> Conversie
            </Button>
          )}
          {etapa === 4 && (
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={solicitareNoua} data-testid="crm-fisa-solicitare-noua">
              <RefreshCw className="h-4 w-4 mr-1" /> Credit nou / Refinanțare
            </Button>
          )}
          <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => setRenunta(true)} data-testid="crm-fisa-renunta">
            <XCircle className="h-4 w-4 mr-1" /> Renunță
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Contact + cerere */}
        <Card>
          <CardHeader><CardTitle className="text-base">Date & cerere</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Telefon:</span> {c.telefon || '—'}</div>
              <div><span className="text-gray-500">Email:</span> {c.email || '—'}</div>
              <div><span className="text-gray-500">Consultant:</span> {c.consultantName || '—'}</div>
              <div><span className="text-gray-500">Sursă:</span> {sol.sursa || '—'}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tip credit</Label>
                <select className="w-full border rounded-md h-9 px-2 text-sm" value={sol.tipCredit || ''}
                  onChange={(e) => setField('tipCredit', e.target.value)} data-testid="crm-fisa-tipcredit">
                  <option value="">—</option>
                  {(settings?.tipuriCredit || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Sumă solicitată (RON)</Label>
                <Input type="number" value={sol.sumaSolicitata || ''} onChange={(e) => setField('sumaSolicitata', e.target.value)} data-testid="crm-fisa-suma" />
              </div>
              <div>
                <Label className="text-xs">Preț imobil</Label>
                <Input type="number" value={sol.pretImobil || ''} onChange={(e) => setField('pretImobil', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Avans</Label>
                <Input type="number" value={sol.avans || ''} onChange={(e) => setField('avans', e.target.value)} />
              </div>
            </div>
            <div className="text-xs text-gray-600">LTV estimat: <strong>{sol.ltv != null ? `${sol.ltv}%` : '—'}</strong></div>
            <div>
              <Label className="text-xs">Analiză intermediar</Label>
              <textarea className="w-full border rounded-md p-2 text-sm" rows={2}
                value={sol.analizaIntermediar || ''} onChange={(e) => setField('analizaIntermediar', e.target.value)} />
            </div>
          </CardContent>
        </Card>

        {/* Etapa / pas / acte / banci */}
        <Card>
          <CardHeader><CardTitle className="text-base">Etapă & acțiuni</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Status (etapa {etapa})</Label>
                <select className="w-full border rounded-md h-9 px-2 text-sm" value={sol.status}
                  onChange={(e) => setField('status', e.target.value)} data-testid="crm-fisa-status">
                  {(STATUS_BY_STAGE[etapa] || []).map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </div>
            </div>
            {etapa !== 4 && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Următorul pas</Label>
                  <Input value={sol.urmatorulPas?.text || ''} onChange={(e) => setPas('text', e.target.value)}
                    placeholder="ex: Sun clientul" data-testid="crm-fisa-pas-text" />
                </div>
                <div>
                  <Label className="text-xs">Data</Label>
                  <Input type="date" value={sol.urmatorulPas?.data || ''} onChange={(e) => setPas('data', e.target.value)} data-testid="crm-fisa-pas-data" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Acte semnate (necesare pentru etapa 3)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {Object.keys(ACTE_LABELS).map((k) => {
                  const signed = sol.acte?.[k]?.semnat;
                  return (
                    <button key={k} onClick={() => setActa(k, !signed)}
                      className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md border ${signed ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}
                      data-testid={`crm-fisa-acta-${k}`}>
                      {signed ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />} {ACTE_LABELS[k]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs">Bănci alese</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {banci.map((b) => {
                  const on = (sol.banciAlese || []).includes(b);
                  return (
                    <button key={b} onClick={() => toggleBanca(b)}
                      className={`text-xs px-2 py-1 rounded-md border ${on ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                      {b}
                    </button>
                  );
                })}
              </div>
            </div>
            <Button onClick={save} disabled={saving} className="w-full" data-testid="crm-fisa-save">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Se salvează…' : 'Salvează'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Codebitori (persoane pe client, legate la creditul curent) */}
      <CoDebitoriPanel
        api={api}
        clientId={clientId}
        solicitareId={sol.id}
        coDebitori={c.coDebitori || []}
        atasati={sol.coDebitoriIds || []}
        onChanged={() => { load(); onChanged && onChanged(); }}
      />

      {/* Dosare la bancă (Faza 2) */}
      {(etapa === 3 || etapa === 4) && (
        <DosarePanel
          api={api}
          solicitareId={sol.id}
          banci={(sol.banciAlese && sol.banciAlese.length) ? sol.banciAlese : (settings?.banci || [])}
          canAdd={etapa === 3}
          onChanged={() => { load(); onChanged && onChanged(); }}
        />
      )}

      {/* Credite */}
      {(data.credite.length > 0 || etapa === 4) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {data.credite.length > 0 ? `Credite (${data.credite.length})` : 'Creditul clientului'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.credite.length === 0 && (
              <p className="text-sm text-gray-500" data-testid="crm-credit-missing">
                Clientul e în retenție, dar creditul nu e încă înregistrat. Completează banca, dobânda și
                perioada fixă, ca să primești alertele înainte să expire dobânda.
              </p>
            )}
            {data.credite.map((cr) => (
              <CreditCard key={cr.id} api={api} credit={cr}
                banci={settings?.banci || []}
                onChanged={() => { load(); onChanged && onChanged(); }} />
            ))}
            {data.credite.length === 0 && (
              <CreditCard api={api} credit={null} solicitareId={sol.id}
                banci={settings?.banci || []}
                onChanged={() => { load(); onChanged && onChanged(); }} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Timeline cicluri */}
      <Card>
        <CardHeader><CardTitle className="text-base">Cicluri ({data.solicitari.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {data.solicitari.map((s) => (
            <div key={s.id} className={`text-sm border rounded-lg p-2 ${s.isActive ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <span>{ETAPE[s.etapa]} · {statusLabel(s.status)} {s.tipIntrare === 'portofoliu' && '· portofoliu'}</span>
                <span className="text-xs text-gray-400">{(s.dataIntrarii || '').slice(0, 10)}</span>
              </div>
              {s.motivRenuntare && <div className="text-xs text-red-600">Renunțat: {s.motivRenuntare}</div>}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Istoric */}
      <Card>
        <CardHeader><CardTitle className="text-base">Istoric (nu se poate edita)</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-1 max-h-64 overflow-auto">
            {data.istoric.map((h) => (
              <div key={h.id} className="text-xs text-gray-600 flex gap-2 border-b py-1">
                <span className="text-gray-400">{(h.data || '').slice(0, 16).replace('T', ' ')}</span>
                <span className="font-medium">{h.userName}</span>
                <span>{h.actiune}</span>
                {(h.din || h.in) && <span className="text-gray-400">{h.din || ''} → {h.in || ''}</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <RenuntaDialog open={renunta} onClose={() => setRenunta(false)}
        motive={(settings?.motive?.[String(etapa)]) || []} onConfirm={doRenunta} />
    </div>
  );
}
